/**
 * Template for creating new exercises
 */
import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import CorrectIcon from "@material-ui/icons/ThumbUp";
import IncorrectIcon from "@material-ui/icons/ThumbDown";
import Button from "@material-ui/core/Button";
import Fab from "@material-ui/core/Fab";
import { img } from "../../config";
import { getResource } from "../../tools";
import "./Dictionary.scss";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& > div": {
      width: 700,
      maxWidth: "100%",
      background: "#fff",
      padding: 20,
      maxHeight: "100%",
      overflow: "auto",
    },
    "& h3": {
      color: theme.palette.primary.main,
      textAlign: "center",
      marginTop: 0,
    },
  },
  menu: {
    maxWidth: "100%",
    minHeight: 450,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    "& > div": {
      border: "3px solid #C3CCE9",
      width: 400,
      maxWidth: "100%",
      textAlign: "center",
      cursor: "pointer",
      borderRadius: 5,
      marginBottom: 5,
      padding: 5,
      background: "#fff",
      "&:hover": {
        background: "#EFF3FA",
      },
    },
    "& img": {
      maxWidth: 400,
      marginBottom: 20,
    },
  },
  cards: {
    "& > h3": {
      textAlign: "center",
    },
  },
  dict: {
    "& h3": {
      marginBottom: "0.25em",
    },
  },
  dictTrans: {
    textAlign: "center",
    marginTop: 0,
  },
  dictTable: {
    margin: "0 auto",
    borderCollapse: "collapse",
    fontSize: "0.85em",
    "& tr:hover": {
      background: "#eee",
    },
    "& td": {
      border: "1px solid #aaa",
      padding: "0.125em 0.5em",
      width: "50%",
    },
  },
  sample: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.secondary.main,
    fontFamily: theme.typography.body1.fontFamily,
  },
  incorrect: {
    color: theme.palette.error.main,
  },
  correct: {
    color: theme.palette.success.main,
  },
  done: {
    color: "#1d9021",
    marginRight: 5,
    float: "right",
  },
  notDone: {
    color: "#ccc",
    marginRight: 5,
    float: "right",
  },
  action: {
    textAlign: "center",
    verticalAlign: "middle",
    marginTop: 20,
    "& button": {
      margin: "0 3px",
      verticalAlign: "middle",
    },
    "& > span": {
      display: "inline-block",
      minWidth: 53,
      verticalAlign: "middle",
      userSelect: "none",
    },
  },
  "@media(max-width: 850px)": {},
}));

let timeout1 = 0;
const bg1 = `url("${img("flip-back4.jpg")}")`;
const bg2 = `url("${img("flip-back3.jpg")}")`;

function Dictionary(props) {
  const classes = useStyles();
  const [state, setState] = useState("top-menu");
  const [data, setData] = useState([]);
  const [dictNo, setDictNo] = useState(0);
  const [flashcards, setFlashcards] = useState([]);
  const [finished, setFinished] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const { startStats, action, type } = props.exerciseProps;
  const ref = useRef(null);

  useEffect(() => {
    const url = "Dictionary/category.json";
    getResource(url)
      .then((response) => {
        setData(response);
      })
      .catch((e) => console.log(e));
    init();
    return () => {
      clearTimeout(timeout1);
    };
    // eslint-disable-next-line
  }, []);

  const init = () => {
    action();
    startStats(type);
    setFinished(false);
  };

  const correct = () => {
    action();
    setIsActive(false);
    if (flashcards.length > 1) {
      const newFlashcards = [...flashcards];
      ref.current.classList.remove("active");
      timeout1 = setTimeout(() => {
        newFlashcards.shift();
        setFlashcards(newFlashcards);
      }, 100);
    } else {
      setFinished(true);
    }
  };

  const incorrect = () => {
    action();
    setIsActive(false);
    ref.current.classList.remove("active");
    if (flashcards.length > 1) {
      const newFlashcards = [...flashcards];
      timeout1 = setTimeout(() => {
        const currentCard = newFlashcards.shift();
        newFlashcards.push(currentCard);
        setFlashcards(newFlashcards);
      }, 100);
    }
  };

  const showDict = (idx) => {
    action();
    setDictNo(idx);
    setState("dict");
    setFinished(false);
  };

  const renderMenu = () => {
    return (
      <div className={classes.menu}>
        <img src={img("Flashcards.jpg")} alt="" />
        <div onClick={() => setState("dict-menu")}>
          <span>Słownik</span>
        </div>
        <div onClick={() => setState("flashc-menu")}>
          <span>Fiszki</span>
        </div>
      </div>
    );
  };

  const renderDictMenu = () => {
    return (
      <>
        <div className={classes.menu}>
          <h3>Słownik / Dictionary</h3>
          {data.map((ex, idx) => {
            return (
              <div key={idx} onClick={() => showDict(idx)}>
                <span>{ex.category}</span>
              </div>
            );
          })}
        </div>
        <div className={classes.action}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setState("top-menu")}
          >
            Powrót
          </Button>
        </div>
      </>
    );
  };

  const renderDict = () => {
    const dict = data[dictNo];
    return (
      <div className={classes.dict}>
        <h3>{dict.category}</h3>
        <p className={classes.dictTrans}>({dict.trans})</p>
        <table className={classes.dictTable}>
          <tbody>
            {dict.words.map((word, idx) => (
              <tr key={idx}>
                <td>{word.pl}</td>
                <td>{word.en}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={classes.action}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setState("dict-menu")}
          >
            Powrót
          </Button>
        </div>
      </div>
    );
  };

  const renderFlashcardsMenu = () => {
    return (
      <>
        <div className={classes.menu}>
          <h3>Flashcards / Fiszki</h3>
          {data.map((ex, idx) => {
            return (
              <div key={idx} onClick={() => showFlashcard(idx)}>
                <span>{ex.category}</span>
              </div>
            );
          })}
        </div>
        <div className={classes.action}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setState("top-menu")}
          >
            Powrót
          </Button>
        </div>
      </>
    );
  };

  const showFlashcard = (idx) => {
    setDictNo(idx);
    setFinished(false);
    ref.current && ref.current.classList.remove("active");
    setFlashcards(shuffle(data[idx].words));
    setState("flashcards");
  };

  const renderFlashcards = () => {
    if (!flashcards.length) return "";
    return (
      <div className={classes.dict}>
        <h3>{data[dictNo].category}</h3>
        <p className={classes.dictTrans}>({data[dictNo].trans})</p>
        {drawCard()}
        {finished && (
          <div className={classes.action}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setState("flashc-menu")}
            >
              Powrót
            </Button>
          </div>
        )}
      </div>
    );
  };

  const flipCard = () => {
    ref.current.classList.add("active");
    // play(flashcards[0].fileName);
    timeout1 = setTimeout(() => {
      setIsActive(true);
    }, 250);
  };

  const drawCard = () => {
    const word = flashcards[0];
    return (
      <div className="tw-flip-cards">
        <div ref={ref} className="tw-flip-card">
          <div className="tw-flip-card-inner">
            <div className="tw-flip-card-front" onClick={flipCard}>
              <div style={{ backgroundImage: bg1 }}>
                <span>{word.pl}</span>
              </div>
            </div>
            <div className="tw-flip-card-back">
              <div style={{ backgroundImage: bg2 }}>
                <span>{word.en}</span>
              </div>
            </div>
          </div>
        </div>
        <div
          className="tw-actions"
          style={isActive ? { opacity: 1 } : { opacity: 0 }}
        >
          {!finished && (
            <Fab
              size="large"
              color="secondary"
              className="tw-incorrect"
              onClick={incorrect}
              disabled={!isActive}
            >
              <IncorrectIcon />
            </Fab>
          )}
          {!finished && (
            <Fab
              size="large"
              color="secondary"
              className="tw-correct"
              onClick={correct}
              disabled={!isActive}
            >
              <CorrectIcon />
            </Fab>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.wrapper}>
      <div>
        {state === "top-menu" && renderMenu()}
        {state === "dict-menu" && renderDictMenu()}
        {state === "flashc-menu" && renderFlashcardsMenu()}
        {state === "dict" && renderDict()}
        {state === "flashcards" && renderFlashcards()}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    media: state.media,
    mobile: state.mobile,
  };
};

function shuffle(array) {
  var currentIndex = array.length,
    temporaryValue,
    randomIndex;
  while (0 !== currentIndex) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }
  return array;
}

export default connect(mapStateToProps)(Dictionary);
